
import {
  faArrowRightLong,
  faCalendarXmark,
  faClock,
  faFence,
  faInfoCircle,
  faLocationArrow,
  faPlus,
  faStarShooting
} from '@fortawesome/pro-regular-svg-icons'
import distance from '@turf/distance'

import BookingEntity from '~/lib/entities/booking'

export default {
  name: 'FormBooking',

  components: {
    VCalendarDatePicker: () =>
      process.client
        ? import(/* webpackChunkName: "date-picker" */ 'v-calendar/lib/components/date-picker.umd')
        : null
  },

  props: {
    value: {
      type: Object,
      default: () => ({})
    },

    isBooking: {
      type: Boolean,
      default: false
    },

    isNewUser: {
      type: Boolean,
      default: false
    },

    initialPaddockSlug: {
      type: String,
      default: null
    },

    initialDateTime: {
      type: String,
      default: null
    },

    isRescheduling: {
      type: Boolean,
      default: false
    },

    forceShowAllPaddocks: {
      type: Boolean,
      default: false
    },

    isBrochureware: {
      type: Boolean,
      default: false
    },

    isInsideModal: {
      type: Boolean,
      default: false
    },

    isStaff: {
      type: Boolean,
      default: false
    },

    allowPaddockChange: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      fetchFirstMonthAvailabilityApi: new this.$ApiModel(),
      fetchSecondMonthAvailabilityApi: new this.$ApiModel(),
      wishListApi: new this.$ApiModel(),
      fetchTimer: null,
      isFilteringTime: false,
      currentVisibleStartDate: this.$dateNew.now(),
      changedMonthsCount: 0,
      sessionLength: 25,
      hasLoadedVisibleMonths: false,
      filterStartTime: null,
      filterEndTime: null,
      wishlistHistory: [],
      wishlistTimeOption: 'day',
      wishListCustomStartHour: null,
      wishListCustomEndHour: null,
      alternativePaddockSlots: [],
      isSearchingForAlternativeSlots: false,
      isSwitchingPaddock: false,
      timeFilterOptions: [
        {
          label: 'Show all availabile',
          value: false
        },
        {
          label: 'Filter by time range',
          value: true
        }
      ],

      wishlistTimeOptions: [
        {
          label: 'Any time',
          value: 'day'
        },
        {
          label: 'AM',
          value: 'am'
        },
        {
          label: 'PM',
          value: 'pm'
        },
        {
          label: 'Specific time',
          value: 'custom'
        }
      ],

      faPlus,
      faClock,
      faCalendarXmark,
      faLocationArrow,
      faArrowRightLong,
      faStarShooting,
      faFence,
      faInfoCircle
    }
  },

  computed: {
    compModel: {
      // Don't use v-model with a vuex store
      set(newValue) {
        this.$emit('input', newValue)
      },

      get() {
        return this.value
      }
    },

    compValidations() {
      return new BookingEntity().validation
    },

    compDisabledDates() {
      return [...this.compSlotAvailability.invalid]
    },

    compAbleToWishlist() {
      return this.$app.isAuth
    },

    compCalendarAttributes() {
      return [
        {
          key: 'active',
          highlight: {
            style: { backgroundColor: '#000' },
            contentStyle: { color: '#fff' }
          },

          dates: this.compModel.startDate ? this.$dateNew.fromSQL(this.compModel.startDate).toSQLDate() : [],

          order: 3
        },
        {
          key: 'gray',
          highlight: {
            color: 'gray',
            fillMode: 'light',
            class: 'sold-out'
          },

          dates: [...this.compSlotAvailability.none],

          order: 2
        },
        {
          key: 'red',
          highlight: {
            color: 'red',
            fillMode: 'light'
          },

          dates: [...this.compSlotAvailability.few],

          order: 2
        },
        {
          key: 'yellow',
          highlight: {
            color: 'yellow',
            fillMode: 'light'
          },

          dates: [...this.compSlotAvailability.some],

          order: 2
        },
        {
          key: 'green',
          highlight: {
            color: 'green',
            fillMode: 'light'
          },

          dates: [...this.compSlotAvailability.plenty],

          order: 2
        }
      ]
    },

    compSelectedPaddock() {
      return this.$store.getters['paddocks/paddockById'](this.compModel.paddockId)
    },

    // Get list of hour options
    compStartTimeFilterOptions() {
      const options = []

      for (let hour = 6; hour < 21; hour++) {
        options.push({
          label: `${hour}:00`,
          value: hour
        })
      }

      return options
    },

    compEndTimeFilterOptions() {
      const options = []

      const startHour = this.filterStartTime ? this.filterStartTime + 1 : 7

      for (let hour = startHour; hour < 23; hour++) {
        options.push({
          label: `${hour}:00`,
          value: hour
        })
      }

      return options
    },

    compVisibleMonths() {
      if (this.$breakpoint.xs) {
        return 1
      } else {
        return 2
      }
    },

    compAlternateSessionLength() {
      if (this.sessionLength === 25) {
        return 55
      } else {
        return 25
      }
    },

    compSessionLengthOptions() {
      return [
        {
          label: '25 minutes',
          value: 25,
          count: this.compChosenDaySlots[25].length
        },
        {
          label: '55 minutes',
          value: 55,
          count: this.compChosenDaySlots[55].length
        }
      ]
    },

    compSlots() {
      const slots = {
        25: {},
        55: {}
      }

      if (this.fetchFirstMonthAvailabilityApi.response.data?.slots) {
        slots[25] = { ...slots[25], ...this.fetchFirstMonthAvailabilityApi.response.data.slots[25].slotsByDate }
        slots[55] = { ...slots[55], ...this.fetchFirstMonthAvailabilityApi.response.data.slots[55].slotsByDate }
      }

      if (this.fetchSecondMonthAvailabilityApi.response.data?.slots) {
        slots[25] = { ...slots[25], ...this.fetchSecondMonthAvailabilityApi.response.data.slots[25].slotsByDate }
        slots[55] = { ...slots[55], ...this.fetchSecondMonthAvailabilityApi.response.data.slots[55].slotsByDate }
      }

      return slots
    },

    compSlotAvailability() {
      const availability = {
        plenty: [],
        some: [],
        few: [],
        none: [],
        invalid: []
      }

      if (!this.fetchFirstMonthAvailabilityApi.response.data?.slots) {
        return availability
      }

      const sixMonthsFromNow = this.$dateNew.now().plus({ months: 6 })

      Object.entries(this.fetchFirstMonthAvailabilityApi.response.data.slots['25'].slotsByDate).forEach(
        ([dateString, data]) => {
          const date = this.$dateNew.fromSQL(dateString)

          // console.log(dateString, data)

          if (this.$dateNew.now() > date.endOf('day') || sixMonthsFromNow < date) {
            availability.invalid.push(date.toSQLDate())
          } else if (data.percentageSlotsAvailable >= 60) {
            availability.plenty.push(date.toSQLDate())
          } else if (data.percentageSlotsAvailable >= 30) {
            availability.some.push(date.toSQLDate())
          } else if (data.percentageSlotsAvailable === 0) {
            availability.none.push(date.toSQLDate())
          } else {
            availability.few.push(date.toSQLDate())
          }
        }
      )

      if (this.$breakpoint.smAndUp && this.fetchSecondMonthAvailabilityApi.response.data?.slots) {
        Object.entries(this.fetchSecondMonthAvailabilityApi.response.data.slots['25'].slotsByDate).forEach(
          ([dateString, data]) => {
            const date = this.$dateNew.fromSQL(dateString)

            if (this.$dateNew.now() > date.endOf('day') || sixMonthsFromNow < date) {
              availability.invalid.push(date.toSQLDate())
            } else if (data.percentageSlotsAvailable >= 60) {
              availability.plenty.push(date.toSQLDate())
            } else if (data.percentageSlotsAvailable >= 30) {
              availability.some.push(date.toSQLDate())
            } else if (data.percentageSlotsAvailable === 0) {
              availability.none.push(date.toSQLDate())
            } else {
              availability.few.push(date.toSQLDate())
            }
          }
        )
      }

      return availability
    },

    compChosenDaySlots() {
      if (!this.compSlots || !this.compModel.startDate) {
        return {
          25: [],
          55: []
        }
      }

      const dayString = this.$dateNew.fromSQL(this.compModel.startDate).toSQLDate()

      const slots = {
        25: this.compSlots[25]?.[dayString]?.availability || [],
        55: this.compSlots[55]?.[dayString]?.availability || []
      }

      return slots
    },

    compChosenDaySlotsMorning() {
      return this.compChosenDaySlots[this.sessionLength].filter(slot => {
        return parseInt(slot.startAtTime.split(':')[0]) < 12
      })
    },

    compChosenDaySlotsAfternoon() {
      return this.compChosenDaySlots[this.sessionLength].filter(slot => {
        return parseInt(slot.startAtTime.split(':')[0]) >= 12
      })
    }
  },

  created() {
    this.setupModel()

    this.fetchWishlist()
  },

  beforeDestroy() {
    if (this.fetchTimer) {
      clearInterval(this.fetchTimer)
    }
  },

  mounted() {
    if (this.$route.query?.switched) {
      this.isSwitchingPaddock = true

      setTimeout(() => {
        this.isSwitchingPaddock = false
      }, 1500)
    }
  },

  methods: {
    async setupModel() {
      if (this.$store.getters['paddocks/all'].length === 0) {
        await Promise.all([
          this.$store.dispatch('paddocks/fetchAll'),
          this.$store.dispatch('booking/fetchRecentBookings'),
          this.$store.dispatch('booking/fetchPreviousBookings')
        ])
      }

      this.compModel = new BookingEntity({ ...this.value }).model

      await this.$nextTick()

      if (this.initialDateTime && this.initialDateTime !== 'null') {
        this.compModel.startDate = this.$dateNew.fromSQL(this.initialDateTime).toSQLDate()
      }

      if (!this.compModel.startDate) {
        this.compModel.startDate = this.$dateNew.now().plus({ days: 1 }).toSQLDate()
      }

      if (this.initialPaddockSlug && this.initialPaddockSlug === 'all') {
        this.compModel.paddockId = null
      } else if (this.initialPaddockSlug) {
        this.compModel.paddockId = this.$store.getters['paddocks/paddockBySlug'](this.initialPaddockSlug)?.id
      } else if (!this.compModel.paddockId && this.$store.getters['booking/lastBookedPaddock']) {
        this.compModel.paddockId = this.$store.getters['booking/lastBookedPaddock'].id
      }

      if (this.isRescheduling && this.compModel.sessionLength) {
        this.sessionLength = this.compModel.sessionLength
      } else if (this.$route.query?.length) {
        this.sessionLength = parseInt(this.$route.query.length)
      }

      await this.$nextTick()

      // We need to wait here as setTime relies on compSelectedPaddock
      if (this.initialDateTime && this.initialDateTime !== 'null') {
        const time = this.$dateNew.fromSQL(this.initialDateTime).toLocaleString(this.$dateNew.TIME_24_SIMPLE)

        if (time && time !== '00:00') {
          this.setTime(time)
        }
      }
    },

    async fetchAvailableSessions() {
      if (!this.compModel.paddockId) {
        return false
      }

      const firstMonthstartAt = this.currentVisibleStartDate.startOf('month')
      const firstMonthEndAt = firstMonthstartAt.endOf('month')

      const secondMonthStartAt = this.currentVisibleStartDate.plus({ months: 1 }).startOf('month')
      const secondMonthEndAt = secondMonthStartAt.endOf('month')

      if (this.$breakpoint.xs) {
        await this.$api.booking(this.fetchFirstMonthAvailabilityApi).getAvailability({
          startAt: firstMonthstartAt.toSQL(),
          endAt: firstMonthEndAt.toSQL(),
          paddockId: this.compModel.paddockId,
          filterStartTime: this.filterStartTime,
          filterEndTime: this.filterEndTime
        })
      } else {
        await Promise.allSettled([
          await this.$api.booking(this.fetchFirstMonthAvailabilityApi).getAvailability({
            startAt: firstMonthstartAt.toSQL(),
            endAt: firstMonthEndAt.toSQL(),
            paddockId: this.compModel.paddockId,
            filterStartTime: this.filterStartTime,
            filterEndTime: this.filterEndTime
          }),
          await this.$api.booking(this.fetchSecondMonthAvailabilityApi).getAvailability({
            startAt: secondMonthStartAt.toSQL(),
            endAt: secondMonthEndAt.toSQL(),
            paddockId: this.compModel.paddockId,
            filterStartTime: this.filterStartTime,
            filterEndTime: this.filterEndTime
          })
        ])
      }

      this.hasLoadedVisibleMonths = true

      if (!this.fetchTimer) {
        this.fetchTimer = setInterval(() => {
          this.$store.dispatch('booking/fetchBasket')
          this.fetchAvailableSessions()
        }, 10000)
      }
    },

    async resetAndFetch() {
      await this.$nextTick()

      this.$scroll.intoViewIfNeeded(this.$refs.chosenPaddock)
      this.fetchAvailableSessions()

      this.changedMonthsCount = 0

      setTimeout(() => {
        this.hasLoadedVisibleMonths = false

        this.compModel.startTime = null
      }, 500)
    },

    bookAndReset() {
      this.$emit('bookAndReset')
    },

    bookAndCheckout() {
      this.$emit('bookAndCheckout')
    },

    findNearMe() {
      this.$modal.open('paddock/find-paddock', {}, chosenPaddock => {
        this.selectNewPaddock(chosenPaddock.id)
      })
    },

    selectNewPaddock(paddockId, { dateTime = null, length = null } = {}) {
      if (!length) {
        length = this.sessionLength
      }

      if (!dateTime) {
        dateTime = this.compModel.startDate
      }

      this.compModel.paddockId = paddockId
      // Change url
      const bookPrefix = this.$app.isBrochureware ? '/book' : '/platform/book'

      this.$router.push(
        `${bookPrefix}/${
          this.$store.getters['paddocks/paddockById'](paddockId).slug
        }?date=${dateTime}&length=${length}&switched=true`
      )

      this.resetAndFetch()
    },

    async setTime(time) {
      this.compModel.startTime = time
      this.compModel.sessionLength = this.sessionLength
      this.compModel.price = this.compSelectedPaddock[`price${this.sessionLength}`]

      await this.$nextTick()

      this.$scroll.intoViewIfNeeded(document.querySelector('.book-cta'))
    },

    toggleSessionLength() {
      if (this.sessionLength === 25) {
        this.sessionLength = 55
      } else {
        this.sessionLength = 25
      }
    },

    updateFromPage(page) {
      const newVisibleStartDate = this.$dateNew.now().set({
        month: page.month,
        year: page.year
      })

      // v-calendar can emit `update:from-page` more than once during
      // initialisation for the same month. Only treat it as a user month
      // navigation (and clear the pre-selected date) when the visible month
      // actually changes, otherwise the pre-selected day (e.g. tomorrow) gets
      // wiped on load and its availability never appears.
      const isMonthChange =
        newVisibleStartDate.month !== this.currentVisibleStartDate.month ||
        newVisibleStartDate.year !== this.currentVisibleStartDate.year

      this.currentVisibleStartDate = newVisibleStartDate

      if (this.changedMonthsCount > 0 && isMonthChange) {
        this.compModel.startDate = null
        this.compModel.startTime = null
      }

      this.hasLoadedVisibleMonths = false

      this.changedMonthsCount++

      this.fetchAvailableSessions()
    },

    onTimeFilterChange() {
      if (!this.isFilteringTime) {
        this.filterStartTime = null
        this.filterEndTime = null

        this.fetchAvailableSessions()
      }

      if (this.filterStartTime && this.filterEndTime) {
        this.$analytics.addEvent('Filtered by time range', {
          start: this.filterStartTime,
          end: this.filterEndTime
        })

        this.fetchAvailableSessions()
      }
    },

    async fetchWishlist() {
      if (!this.$app.isAuth) {
        return false
      }

      try {
        const { response } = await this.$api.booking(this.fetchWishlistApi).getWishlist()

        if (response.data.length) {
          this.wishlistHistory = response.data.map(item => `${item.date}-${item.paddockId}`)
        }
      } catch (error) {
        this.$log.error('Error fetching wishlist', error)
      }
    },

    async addWishListDate() {
      if (this.wishlistTimeOption === 'custom') {
        if (!this.wishListCustomStartHour || !this.wishListCustomEndHour) {
          this.$notify.error('Please select a start and end time')
          return false
        }
      }

      try {
        await this.$api.booking(this.wishListApi).addWishListDate({
          date: this.compModel.startDate,
          timeOption: this.wishlistTimeOption,
          startHour: this.wishListCustomStartHour,
          endHour: this.wishListCustomEndHour,
          paddockId: this.compModel.paddockId
        })

        this.wishlistHistory.push(`${this.compModel.startDate}-${this.compModel.paddockId}`)
      } catch (error) {
        this.$log.error('Error adding wish list date', error)

        this.$notify.error('Error adding to wishlist, please reload and try again')
      }
    },

    async removeWishListDate() {
      try {
        await this.$api.booking(this.wishListApi).removeWishListDate({
          date: this.compModel.startDate,
          paddockId: this.compModel.paddockId
        })

        // Remove from wishlist history
        this.wishlistHistory = this.wishlistHistory.filter(
          item => item !== `${this.compModel.startDate}-${this.compModel.paddockId}`
        )
      } catch (error) {
        this.$log.error('Error removing wish list date', error)

        this.$notify.error('Error removing wishlist, please reload and try again')
      }
    },

    async onDateSelected() {
      await this.$nextTick()

      // Reset selected time in case we've clicked a sold out day
      this.compModel.startTime = null

      // Scroll to time picker
      if (this.$refs.timeSlots) {
        this.$scroll.intoViewIfNeeded(this.$refs.timeSlots)
      }

      if (this.$refs.noTimeSlots) {
        this.$scroll.intoViewIfNeeded(this.$refs.noTimeSlots)
      }

      this.checkForAvailabilityAtNearbyPaddocks(this.compModel.startDate)
    },

    onSessionLengthChange() {
      this.checkForAvailabilityAtNearbyPaddocks(this.compModel.startDate)
    },

    viewPaddock(paddockId = null) {
      let paddockSlug = this.compSelectedPaddock.slug

      if (paddockId) {
        paddockSlug = this.$store.getters['paddocks/paddockById'](paddockId).slug
      }

      if (this.$app.isBrochureware) {
        this.$redirect.to(`/paddocks/${paddockSlug}`)
      } else {
        this.$redirect.to(`/platform/paddocks/${paddockSlug}`)
      }
    },

    async checkForAvailabilityAtNearbyPaddocks(sessionDate) {
      await this.$nextTick()

      // If we don't have any availability then we need to check nearby paddocks
      if (!this.compChosenDaySlots?.[this.sessionLength]?.length) {
        // Find 2 nearest paddocks
        const nearestPaddocks = this.findNearestPaddocks()

        this.isSearchingForAlternativeSlots = true
        const allAlternativePaddockSlots = []

        // Check availability at nearest paddocks
        await Promise.all(
          nearestPaddocks.map(async paddock => {
            const paddockAvailabilityApi = new this.$ApiModel()

            const availability = await this.$api.booking(paddockAvailabilityApi).getAvailability({
              startAt: `${sessionDate} 00:00:00`,
              endAt: `${sessionDate} 23:59:59`,
              paddockId: paddock.id,
              filterStartTime: this.filterStartTime,
              filterEndTime: this.filterEndTime
            })

            if (availability?.slots?.[this.sessionLength]?.slotsByDate?.[sessionDate]?.availability?.length) {
              allAlternativePaddockSlots.push({
                paddock,
                distanceMiles: paddock.distanceMiles,
                slots: availability.slots[this.sessionLength].slotsByDate[sessionDate].availability
              })
            }
          })
        )

        // Order by distance
        if (allAlternativePaddockSlots.length) {
          this.$set(
            this,
            'alternativePaddockSlots',
            allAlternativePaddockSlots.sort((a, b) => a.distanceMiles - b.distanceMiles).slice(0, 2)
          )
        }
      } else {
        this.$set(this, 'alternativePaddockSlots', [])
      }

      this.isSearchingForAlternativeSlots = false
    },

    findNearestPaddocks() {
      return this.$store.getters['paddocks/all']
        .map(paddock => {
          const distanceMiles = distance(
            this.compSelectedPaddock.entranceCoordinates,
            paddock.entranceCoordinates,
            {
              units: 'miles'
            }
          )

          return {
            id: paddock.id,
            name: paddock.name,
            fullAddress: paddock.fullAddress,
            distanceMiles: Math.round(distanceMiles)
          }
        })
        .filter(paddock => paddock.id !== this.compSelectedPaddock.id && paddock.distanceMiles < 10)
    },

    // User has chosen a time from an alternative paddock
    switchPaddock({ paddockId, startDate, startTime, sessionLength }) {
      this.$analytics.addEvent('Selected non sold out time from alternative paddock')

      this.selectNewPaddock(paddockId, {
        dateTime: `${startDate} ${startTime}:00`,
        length: sessionLength
      })
    }
  }
}
